/* eslint-disable no-nested-ternary */
/* eslint-disable no-shadow */
import React, { useState } from "react";
import { Form, Button } from "reactstrap";
import gql from "graphql-tag";
import styled from "styled-components";
import { useMutation } from "@apollo/client";
import { store } from "react-notifications-component";
import Loading, { Error } from "../../../../components/Loading";
import WeekDaySelector from "../../../../components/WeekDaySelector";
import WorkerSelector from "../../../../components/Worker/WorkerSelector";
import EnumSelector from "../../../../components/EnumSelector";
import {
  formatConferenceTopic,
  formatConferenceDuration,
  formatFeedback
} from "../../../../utilities/utils";
import {
  WEEKDAY_INTERACTIONS_QUERY,
  MEMBER_LOAD_QUERY
} from "../../../../graphql/queries";
import "react-notifications-component/dist/theme.css";

const UPSERT_CONFERENCE_MUTATION = gql`
  mutation UPSERT_CONFERENCE_MUTATION(
    $id: Int
    $teamId: Int!
    $sprintId: Int!
    $weekNumber: Int!
    $weekDay: Weekday!
    $conferenceDuration: ConferenceDuration!
    $conferenceTopic: ConferenceTopic!
    $feedback: Feedback!
    $teamWorkerId: Int!
  ) {
    upsertConference(
      id: $id
      teamId: $teamId
      sprintId: $sprintId
      weekNumber: $weekNumber
      weekDay: $weekDay
      duration: $conferenceDuration
      topic: $conferenceTopic
      feedback: $feedback
      teamWorkerId: $teamWorkerId
    ) {
      id
    }
  }
`;

const StyledForm = styled(Form)`
  display: grid;
  grid-template-columns: 200px 200px;
  grid-gap: 10px;

  > .last {
    grid-column: 1 / -1;
  }
`;

const ConferenceForm = ({
  toggleModal,
  teamId,
  weekDay,
  type,
  sprintId,
  weekNumber,
  data
}) => {
  const [form, setValues] = useState({
    id: data ? data.id : null,
    teamId,
    sprintId,
    weekNumber,
    weekDay,
    type,
    conferenceDuration: data ? data.conferenceDetail.duration : null,
    conferenceTopic: data ? data.conferenceDetail.topic : null,
    feedback: data ? data.conferenceDetail.feedback : null,
    teamWorkerId: data ? data.conferenceDetail.teamWorker.id : null
  });

  const queriesToRefetch = [
    {
      query: WEEKDAY_INTERACTIONS_QUERY,
      variables: { teamId, sprintId, weekNumber }
    },
    {
      query: MEMBER_LOAD_QUERY,
      variables: { teamId, sprintId }
    }
  ];

  const [
    upsertConference,
    { loading: mutationLoading, error: mutationError }
  ] = useMutation(UPSERT_CONFERENCE_MUTATION, {
    refetchQueries: queriesToRefetch
  });

  if (mutationError) return <Error error={mutationError} />;

  const onChangeOption = (value, action) => {
    setValues({
      ...form,
      [action.name]: value.value
    });
  };

  const onSubmitHandler = e => {
    e.preventDefault();

    if (form.weekDay === null) {
      showNotification("Please select a day of the week");
      return;
    }

    if (form.conferenceTopic === null) {
      showNotification("Please select a topic");
      return;
    }

    if (form.conferenceTopic === null) {
      showNotification("Please select a topic");
      return;
    }

    if (form.teamWorkerId === null) {
      showNotification("Please select a team member");
      return;
    }

    if (form.conferenceDuration === null) {
      showNotification("Please select duration");
      return;
    }

    if (form.feedback === null) {
      showNotification("Please select feedback option");
      return;
    }

    form.teamWorkerId = parseInt(form.teamWorkerId, 10);

    upsertConference({ variables: { ...form } }).then(response => {
      if (response.data.upsertConference !== null) {
        toggleModal();
      } else {
        showNotification(
          "Cannot schedule more than 8 hours of team member capacity per day or create duplicate calendar entries."
        );
      }
    });
  };

  const showNotification = text => {
    store.addNotification({
      message: text,
      container: "top-right",
      type: "default",
      animationIn: ["animated", "bounceIn"],
      animationOut: ["animated", "bounceOut"],
      dismiss: { duration: 5000 },
      width: 294,
      content: <div className="notification-sim">{text}</div>
    });
  };

  return (
    <StyledForm id="conference-form" onSubmit={onSubmitHandler}>
      <WeekDaySelector
        controlName="weekDay"
        defaultValue={weekDay}
        onChange={onChangeOption}
      />

      <EnumSelector
        enumType="ConferenceTopic"
        title="Topic"
        controlName="conferenceTopic"
        formatProvider={formatConferenceTopic}
        defaultValue={form.conferenceTopic}
        onChange={onChangeOption}
      />

      <WorkerSelector
        controlName="teamWorkerId"
        teamId={teamId}
        defaultValue={form.teamWorkerId}
        onChange={onChangeOption}
      />

      <EnumSelector
        enumType="ConferenceDuration"
        title="Duration"
        controlName="conferenceDuration"
        formatProvider={formatConferenceDuration}
        defaultValue={form.conferenceDuration}
        onChange={onChangeOption}
      />

      <EnumSelector
        enumType="Feedback"
        title="Feedback"
        controlName="feedback"
        formatProvider={formatFeedback}
        defaultValue={form.feedback}
        onChange={onChangeOption}
      />

      <Button
        disabled={
          mutationLoading ||
          (data && Object.values(form).some(value => value === null))
        }
        className="last"
        color="primary"
        type="submit"
      >
        {mutationLoading ? (
          <Loading text={data ? "Updating" : "Saving"} />
        ) : data ? (
          "Update"
        ) : (
          "Save"
        )}
      </Button>
    </StyledForm>
  );
};

export default ConferenceForm;
